import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';
import { Typography, useMediaQuery } from '@mui/material';

// Utils
import { capitalize } from 'utils/helpers/format';
import { useUserRolePermissions } from 'hooks';

// Components
import { MDialog, PlaceholderPage } from 'common';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import Sidebar from './sidebar/Sidebar.connector';
import CardsPanel from './cards-panel/CardsPanel.connector';
import { AUTH_SEQUENCING_ROUTES, isAuthSequencingUpdatesEnabled } from '../utils/const';
import MobileDrawerConnector from '../mobile-drawer/MobileDrawer.connector';
import SequencingWizard from './sequencing-wizard/SequencingWizardContext';
import InitialStep from './sequencing-wizard/InitialStep';

// Steps
const steps = {
  last: [
    {
      id: 1,
      stepLabel: 'Inventory Owner',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  primary: [
    {
      id: 1,
      stepLabel: 'Inventory Owner',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  remove: [
    {
      id: 1,
      stepLabel: 'Inventory Owners',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Future Date',
    },
    {
      id: 5,
      stepLabel: 'Summary',
    },
    {
      id: 6,
      stepLabel: 'Progress',
    },
  ],
  sequence: [
    {
      id: 1,
      stepLabel: 'Inventory Owners',
    },
    {
      id: 2,
      stepLabel: 'Products',
    },
    {
      id: 3,
      stepLabel: 'Locations',
    },
    {
      id: 4,
      stepLabel: 'Sequence Authorization',
    },
    {
      id: 5,
      stepLabel: 'Future Date',
    },
    {
      id: 6,
      stepLabel: 'Summary',
    },
    {
      id: 7,
      stepLabel: 'Progress',
    },
  ],
};

export const AUTH_SEQ_PERSISTED_FILTER_KEYS = ['nameShort', 'relatedIds', 'searchTerm', 'state'];

const isPersistedAuthSeqFilterApplied = value => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (R.isNil(value)) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized !== '' && normalized !== 'any' && normalized !== 'all';
};

export const hasPersistedAuthSeqFilters = persistedFilters =>
  AUTH_SEQ_PERSISTED_FILTER_KEYS.some(key =>
    isPersistedAuthSeqFilterApplied(persistedFilters?.[key])
  );

const AuthorizationSequencing = ({
  currentSelection,
  filters,
  filtersOpen,
  locationsLoading,
  productsLoading,
  resetFilters,
  setFilters,
  setFiltersOpen,
}) => {
  // viewBy is based on the URL, can be accessed like this anywhere in this section
  const { viewBy } = useParams();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { isSubscriber } = useUserRolePermissions();
  const isLoading = locationsLoading || productsLoading;
  // Feature flag: REACT_APP_LD_SEQUENCING_AUTH_LIST
  const isAuthSequencingUpdatesFlagOn = isAuthSequencingUpdatesEnabled();
  const hasRestoredFiltersRef = useRef(false);

  // Persist viewBy when it changes
  useEffect(() => {
    if (!isAuthSequencingUpdatesFlagOn) return;
    try {
      localStorage.setItem('authorization-sequencing.view', viewBy);
    } catch (e) {
      // Silently fail
    }
  }, [isAuthSequencingUpdatesFlagOn, viewBy]);

  // ── Feature Flags ────────────────────────────────────────────────────────────
  // These flags gate the Authorization Sequencing Wizard button.
  //
  // ✅ TO KEEP THE FEATURE & RETIRE THE FLAGS (permanent rollout):
  //    Replace each EvaluateFlag(...) with its "retire" value below,
  //    then delete the unused constants and env vars.
  //
  // ↩️  TO REVERT TO ORIGINAL BEHAVIOR (remove the feature entirely):
  //    Pass hardcoded values directly in JSX instead:
  //      isSequencingWizardEnabled={false}  ← hides the button for everyone
  //      hasWizardAccess={false}            ← (no-op when hidden, but kept for prop consistency)
  //    Then delete all flag constants, env vars, and defaults.js entries.

  // Master toggle for the Authorization Sequencing Wizard feature.
  // When ON: the "Launch Sequencing Wizard" button is rendered for all users (subject to access below).
  // When OFF: the button is hidden entirely for everyone, regardless of subscription.
  // retire → true
  const isSequencingWizardEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_WIZARD
  );

  // Controls whether the wizard is accessible to all customers or only Swift+ subscribers.
  // When ON: every user can open and use the wizard (button enabled for all).
  // When OFF: only Swift+ subscribers can use the wizard (button disabled with tooltip for non-subscribers).
  // retire → true
  const isSequencingWizardForAllCustomers = EvaluateFlag(
    process.env.REACT_APP_LD_AUTHORIZATION_SEQUENCING_WIZARD_FOR_ALL_CUSTOMERS
  );

  // Derived access: wizard is accessible if the AllCustomers flag is ON, or user is a Swift+ subscriber.
  const hasWizardAccess = isSequencingWizardForAllCustomers || isSubscriber;

  // State values for the Sequencing Wizard
  // sets open
  const [wizardOpen, setWizardOpen] = useState(false);
  // sets initial component inside the drawer
  const [children, setChildren] = useState(<InitialStep key="initialStep" />);
  // opens the dialog
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reset filters when viewBy changes
  useEffect(() => {
    hasRestoredFiltersRef.current = false;
    resetFilters();
    return () => resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  // Restore filters from localStorage only once per viewBy
  useEffect(() => {
    if (!isAuthSequencingUpdatesFlagOn || hasRestoredFiltersRef.current) {
      return;
    }

    try {
      const storageKey = `authorization-sequencing.filters.${viewBy}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const persistedFilters = JSON.parse(stored);
        AUTH_SEQ_PERSISTED_FILTER_KEYS.forEach(key => {
          if (!R.isNil(persistedFilters[key])) {
            setFilters({ name: key, value: persistedFilters[key] });
          }
        });
      }
    } catch (e) {
      // Silently fail - invalid stored data is not critical
    }

    hasRestoredFiltersRef.current = true;
  }, [isAuthSequencingUpdatesFlagOn, setFilters, viewBy]);

  // Save filters to localStorage when they change
  useEffect(() => {
    if (!isAuthSequencingUpdatesFlagOn || !hasRestoredFiltersRef.current) {
      return;
    }

    try {
      const storageKey = `authorization-sequencing.filters.${viewBy}`;
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (e) {
      // Silently fail - localStorage errors are not critical
    }
  }, [filters, isAuthSequencingUpdatesFlagOn, viewBy]);

  // This is the base URL that has no viewBy
  if (R.not(R.includes(viewBy, AUTH_SEQUENCING_ROUTES)))
    return <PlaceholderPage title="Authorization Sequencing" />;

  const tooltip =
    'Sequencing Wizard is available for Affiliates with a Swift+ enrollment. Please select an Affiliate that is enrolled in Swift+';

  return (
    <>
      <Sidebar />
      {lgUp ? (
        <CardsPanel
          hasWizardAccess={hasWizardAccess}
          isSequencingWizardEnabled={isSequencingWizardEnabled}
          setWizardOpen={setWizardOpen}
          tooltip={tooltip}
          viewBy={viewBy}
        />
      ) : (
        <MobileDrawerConnector
          activationClick={() => setWizardOpen(true)}
          filtersClick={() => setFiltersOpen(!filtersOpen)}
          hasWizardAccess={hasWizardAccess}
          isSequencingWizardEnabled={isSequencingWizardEnabled}
          panel={
            <CardsPanel
              hasWizardAccess={hasWizardAccess}
              isSequencingWizardEnabled={isSequencingWizardEnabled}
              setWizardOpen={setWizardOpen}
              tooltip={tooltip}
              viewBy={viewBy}
            />
          }
          tooltip={tooltip}
          type={capitalize(viewBy)}
        />
      )}
      {/* bool to render drawer, unmounting will clear state to initial */}
      {wizardOpen && (
        <SequencingWizard
          currentSelection={currentSelection}
          initialStep={0}
          isLoading={isLoading}
          setChildren={setChildren}
          setDialogOpen={() => setDialogOpen(true)}
          setOpen={setWizardOpen}
          steps={steps}
          title="Sequencing Wizard"
        >
          {children}
        </SequencingWizard>
      )}
      <MDialog
        header="Are you sure you want to close the wizard?"
        leftButtonAction={() => setDialogOpen(false)}
        leftButtonText="Back"
        onClose={() => setDialogOpen(false)}
        open={dialogOpen}
        rightButtonAction={() => {
          setWizardOpen(false);
          setDialogOpen(false);
        }}
        rightButtonText="Confirm"
      >
        <Typography variant="body2">
          By closing the wizard you will no longer be able to view the progress of each activation
          however, all requests will continue until complete. Please confirm you want to close the
          wizard.
        </Typography>
      </MDialog>
    </>
  );
};

AuthorizationSequencing.propTypes = {
  currentSelection: shape({}).isRequired,
  filters: shape({
    nameShort: string,
    relatedIds: arrayOf(oneOfType([string, number])),
    searchTerm: string,
    state: string,
  }).isRequired,
  filtersOpen: bool.isRequired,
  locationsLoading: bool.isRequired,
  productsLoading: bool.isRequired,
  resetFilters: func.isRequired,
  setFilters: func.isRequired,
  setFiltersOpen: func.isRequired,
};

export default AuthorizationSequencing;
